import axios from 'axios';
import { logger } from './logger.service';
import UserInterface from '@/interfaces/user/user.interface';
import { SignupDto } from '@/dtos/auth.dto';
import { getOsDevice } from '../utils/common.utils';
import { VerifiedCoupon } from '@/interfaces/user/verified-coupon.interface';
import { capacitorStorageService } from './capacitor-storage.service';

const userApi = {
	// Public API
	checkIfUserExists: async (email: string): Promise<boolean | null> => {
		try {
			const url = `${process.env.NEXT_PUBLIC_API}/users/exists?email=${email}`;
			const response = await axios.get(url);
			return response.data.exists;
		} catch (error: any) {
			logger.error('getUser() error: ', error.response || error);
			return null;
		}
	},

	// Public API
	validateCredentials: async ({
		email,
		password,
		token,
	}: {
		email: string;
		password: string;
		token: string;
	}): Promise<number> => {
		try {
			const url = `${process.env.NEXT_PUBLIC_API}/users/webapp-validate-credentials`;
			await axios.post(url, { email, password, token });
			return 0;
		} catch (error: any) {
			console.log(error);
			return error?.response?.status || 500;
		}
	},

	// Public API
	createUser: async (body: SignupDto | {}): Promise<string> => {
		const deviceInfo = getOsDevice();
		body = { ...body, signupOs: deviceInfo.os, signupDevice: deviceInfo.device };

		const { contentSlug, fromAddin } = await capacitorStorageService.getAndRemoveSignupParams();
		if (contentSlug) body = { ...body, signupContentSlug: contentSlug };
		if (fromAddin) body = { ...body, fromAddin };

		try {
			const url = `${process.env.NEXT_PUBLIC_API}/users`;
			const response = await axios.post(url, body);
			const newUser = response.data;
			return newUser._id;
		} catch (error: any) {
			logger.error('createUser() error: ', error.response || error);
			// sentryLogApiError(error, 'createUser', { body });
			return '';
		}
	},

	// Runs at backend
	webappLogin: async (
		email: string,
		provider: string,
		ipAddress: string,
	): Promise<{ user: UserInterface; jwt: string } | null> => {
		// console.log('ipAddress', ipAddress);
		try {
			const url = `${process.env.NEXT_PUBLIC_API}/users/webapp-login?email=${email}&provider=${provider}&ipAddress=${ipAddress}`;
			const response = await axios.patch(url);
			return response.data;
		} catch (error: any) {
			logger.error('webappLogin() error: ', error.response || error);
			return null;
		}
	},

	// Public API
	sendResetPasswordEmail: async (email: string): Promise<boolean> => {
		const url = `${process.env.NEXT_PUBLIC_API}/users/verification/send-reset-password-email?email=${email}`;
		try {
			await axios.post(url);
			return true;
		} catch (error) {
			return false;
		}
	},

	// Public API, urlCode is encrypted and decrypted at backend
	verifyEmailThroughUrlCode: async (urlCode: string): Promise<string> => {
		const url = `${process.env.NEXT_PUBLIC_API}/users/verification/verify-email`;
		try {
			const response = await axios.post(url, null, {
				headers: {
					'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
				},
				params: {
					urlCode,
				},
			});
			const email = response.data;
			return email;
		} catch (error) {
			return '';
		}
	},

	// Public API, urlCode is encrypted and decrypted at backend
	resetPassword: async ({ urlCode, password }: { urlCode: string; password: string }): Promise<boolean> => {
		const url = `${process.env.NEXT_PUBLIC_API}/users/verification/reset-password`;
		try {
			await axios.post(url, null, {
				headers: {
					'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
				},
				params: {
					urlCode,
					password,
				},
			});
			return true;
		} catch (error) {
			return false;
		}
	},

	signOut: async (email: string, jwt: string): Promise<void> => {
		if (!email) return;
		try {
			const url = `${process.env.NEXT_PUBLIC_API}/users/sign-out?email=${email}`;
			await axios.post(url, null, { headers: { Authorization: `Bearer ${jwt}` } });
		} catch (error) {}
	},

	verifyCoupon: async (email: string, couponCode: string, jwt: string): Promise<VerifiedCoupon | string> => {
		const url = `${process.env.NEXT_PUBLIC_API}/subscriptions/recurring/verify-coupon?email=${email}&couponCode=${couponCode}&planCode=Annual`;
		try {
			const response = await axios.get(url, { headers: { Authorization: `Bearer ${jwt}` } });
			return response.data;
		} catch (error: any) {
			return 'Coupon code is invalid or expired';
		}
	},
};

export default userApi;
